import omit from 'lodash/omit'

import type { PhoneVerificationRequest } from '@app/constants/ApiTypes/requests'

import { placeCookie } from '@app/utils/routing/region'

import { withAbortSignal } from '@app/packages/abortContext/actions'
import { analyticsEvent } from '@app/packages/analytics/declaration'
import { postTOSAcceptance } from '@app/packages/api/v2/actions/tos'
import { getUsersMe, putUsersById } from '@app/packages/api/v2/actions/users'
import type { ApiPlace } from '@app/packages/api/v2/types/entities'
import type { ApiDataResponse } from '@app/packages/api/v2/types/misc'
import type { ProfileUpdateRequest } from '@app/packages/api/v2/types/params'
import { postAuthPhoneBind } from '@app/packages/api/v3/actions/auth'
import { asError } from '@app/packages/asError/asError'
import { Task } from '@app/packages/task/Task'

import { failProfileUpdateSlice, profileUserSelector, setProfileFromUserResponseSlice, startProfileUpdateSlice } from '@app/store/slices/profile'
import { setProfileAvatarsFromResponseSlice } from '@app/store/slices/profile_avatars'
import { syncCookiePlaceIdSlice } from '@app/store/slices/profile_place'
import { setUsersFromResponseSlice } from '@app/store/slices/users'
import { createThunk } from '@app/store/thunk'
import type { ProfileUpdateState } from '@app/store/types/profile'

import { getAnalyticsRouter } from './analytics'
import { callApi, callV2Api } from './api'
import { getAbortControllerService } from './contexts/abortControllerService'
import { getCookies } from './contexts/cookies'
import { withProgressAction } from './initial'
import { getPlaceByRegion } from './region'
import { loginAndStore, refreshSessionCookie } from './session'
import { createTask } from './tasks'
import { refreshUser } from './user'

export function updateProfile(data: ProfileUpdateState) {
  return createThunk(async (dispatch, getState) => {
    const id = profileUserSelector(getState())?.id
    if (!id) throw new Error('Profile is empty')
    const response = await dispatch(putProfileUser(id, convertProfileUpdateStateToProfilePatch(data)))
    await dispatch(refreshSessionCookie())
    return response
  })
}

/** Writes the profile and folds the user it returns; the request state travels with the call. */
export function putProfileUser(id: string, request: ProfileUpdateRequest) {
  return createThunk(async dispatch => {
    dispatch(startProfileUpdateSlice())
    try {
      const resp = await dispatch(callV2Api(putUsersById(id, request)))
      dispatch(setProfileFromUserResponseSlice(resp))
      return resp
    } catch (e) {
      dispatch(failProfileUpdateSlice(asError(e)))
      throw e
    }
  })
}

/** Accepting the terms answers with the whole user, so it lands like a profile read. */
export function acceptTOS() {
  return createThunk(async dispatch => {
    const resp = await dispatch(callV2Api(postTOSAcceptance()))
    dispatch(setProfileFromUserResponseSlice(resp))
    dispatch(setProfileAvatarsFromResponseSlice(resp))
    dispatch(setUsersFromResponseSlice(resp))
    return resp
  })
}

/** The authoritative profile read: three slices take the user it returns as the whole truth. */
export function readProfileUser() {
  return createThunk(async dispatch => {
    const resp = await dispatch(callV2Api(getUsersMe()))
    dispatch(setProfileFromUserResponseSlice(resp))
    dispatch(setProfileAvatarsFromResponseSlice(resp))
    dispatch(setUsersFromResponseSlice(resp))
    return resp
  })
}

export const loginWithPhoneVerificationRequest = ({ phone, code }: PhoneVerificationRequest) =>
  createThunk(async dispatch => {
    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // A phone number was confirmed from the profile.
        type: 'phone_confirmation',
      })).set('amplitude')
    )
    try {
      await dispatch(loginAndStore({ method: 'phone', phone, code }))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The phone confirmation code was accepted.
          type: 'phone_confirmation_success',
        })).set('amplitude')
      )
    } catch (error) {
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The phone confirmation code was refused.
          type: 'phone_confirmation_fail',
        })).set('amplitude')
      )
      throw error
    }
  })

export const loginWithSmsaeroVerification = ({
  session_id,
  verify_token,
  fingerprint_hash,
}: {
  session_id: string
  verify_token: string
  fingerprint_hash?: string | null
}) =>
  createThunk(async dispatch => {
    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // A phone number was confirmed through SMSAero.
        type: 'smsaero_confirmation',
      })).set('amplitude')
    )
    try {
      await dispatch(loginAndStore({ method: 'smsaero', session_id, verify_token, fingerprint_hash }))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The SMSAero confirmation code was accepted.
          type: 'smsaero_confirmation_success',
        })).set('amplitude')
      )
    } catch (error) {
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The SMSAero confirmation code was refused.
          type: 'smsaero_confirmation_fail',
        })).set('amplitude')
      )
      throw error
    }
  })

export const loginWithYandexVerification = ({ code, code_verifier }: { code: string; code_verifier: string }) =>
  createThunk(async dispatch => {
    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // An account was confirmed through Yandex ID.
        type: 'yandex_confirmation',
      })).set('amplitude')
    )
    try {
      await dispatch(loginAndStore({ method: 'yandex', code, code_verifier }))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // Yandex ID confirmed the account.
          type: 'yandex_confirmation_success',
        })).set('amplitude')
      )
    } catch (error) {
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // Yandex ID refused the account.
          type: 'yandex_confirmation_fail',
        })).set('amplitude')
      )
      throw error
    }
  })

export const loginWithEmailCodeVerification = ({ email, code }: { email: string; code: string }) =>
  createThunk(async dispatch => {
    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // An email was confirmed with a code.
        type: 'email_code_verification',
      })).set('amplitude')
    )
    try {
      await dispatch(loginAndStore({ method: 'email', email, code }))
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The email code was accepted.
          type: 'email_code_verification_success',
        })).set('amplitude')
      )
    } catch (error) {
      dispatch(getAnalyticsRouter()).send(
        analyticsEvent(() => ({
          // The email code was refused.
          type: 'email_code_verification_fail',
        })).set('amplitude')
      )
      throw error
    }
  })

/** Binds a verified phone to the current user via v3; the response carries no user, so the profile is refetched. */
export const bindPhone = ({ phone, code }: { phone: string; code: string }) =>
  createThunk(async dispatch => {
    const { data } = await dispatch(callApi(postAuthPhoneBind({ phone, code })))
    await dispatch(refreshUser())
    return data
  })

/** То же, но номер подтверждён «Мобильной авторизацией» SMS Aero — телефон берётся бекендом из ответа MobileID API. */
export const bindPhoneSmsaero = ({
  session_id,
  verify_token,
  fingerprint_hash,
}: {
  session_id: string
  verify_token: string
  fingerprint_hash?: string | null
}) =>
  createThunk(async dispatch => {
    const { data } = await dispatch(callApi(postAuthPhoneBind({ session_id, verify_token, fingerprint_hash })))
    await dispatch(refreshUser())
    return data
  })

export function setPlace(payload: ApiDataResponse<ApiPlace>) {
  return createThunk(async dispatch => {
    const cookies = dispatch(getCookies())
    await placeCookie.set(cookies, payload.data.id)
    dispatch(syncCookiePlaceIdSlice())
  })
}

export function setPlaceByRegion(regionId: string) {
  return createThunk(async dispatch => {
    const abort = dispatch(getAbortControllerService())
    const abortController = abort.createAbortController('place-by-region-abort-controller')
    const placeResponse = await dispatch(getPlaceByRegion(regionId))
    if (abortController.signal.aborted) return

    await dispatch(setPlace(placeResponse))
  })
}

function convertProfileUpdateStateToProfilePatch(state: ProfileUpdateState): ProfileUpdateRequest {
  const patch = Object.fromEntries(
    Object.entries(state).flatMap(([key, value]): [string, any][] => {
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (key as keyof ProfileUpdateState) {
        case 'location': {
          if (!state.location) return []
          return [
            ['address', state.location.address],
            ['latitude', state.location.latitude],
            ['longitude', state.location.longitude],
          ]
        }
        case 'place': {
          const place = state.place
          if (!place) return [['place_id', undefined]]
          return [['place_id', place.place.id]]
        }
        default:
          return [[key, value]]
      }
    })
  ) as ProfileUpdateRequest
  return patch
}

export const getProfileUpdateTask = () =>
  createTask('Profile update task', dispatch =>
    Task.create(ctx => async (update: ProfileUpdateState) => {
      return await dispatch(
        withAbortSignal(
          ctx.abortController.signal,
          withProgressAction(updateProfile(omit({ ...update, birthday: update.birthday || undefined }, 'account_type')))
        )
      )
    })
  )
